import { Button, Divider, MenuItem, Link, ListItemIcon, Tooltip, IconButton, Menu, Box, Typography, TextField as MuiTextField, 
  SvgIcon, Card, CardContent, CardHeader, Drawer, useMediaQuery, FormHelperText, Grid, Stack } from "@mui/material";
import * as Yup from 'yup'
import AddToDriveIcon from '@mui/icons-material/AddToDrive';
import PolicyIcon from '@mui/icons-material/Policy';
import RuleIcon from '@mui/icons-material/Rule';
import QueueIcon from '@mui/icons-material/Queue';
import GradientIcon from '@mui/icons-material/Gradient';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { push } from "connected-react-router";
import { crudGetList, ReduxState, useDeleteWithConfirmController, useGetList, refreshView } from "ra-core";
import React, { FC, useEffect, useRef, useMemo, useCallback, useState, ChangeEventHandler } from "react";
import { useSelector, useDispatch } from "react-redux";
import TextField from "@/components/formik/TextField";
import SelectField from "@/components/formik/SelectField";
import { Link as RouterLink } from 'react-router-dom'
import Confirm from "@/components/widgets/Confirm";
import { MANAGE_DATABASE_OPERATIONS, Permissions_new } from "@/constants";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { RecPatch } from "@/dataProvider/tools";
import { DatabaseResponseDto, EngineResponseDto } from "@/gen/api/backup";
import { DeploymentResponseDto, DeploymentStatus } from "@/gen/api/deploy";
import { EncryptionJobResponseDto as EncryptDbDto, EncryptionSettingChangesResponseDto } from "@/gen/api/encrypt";
import { OnDataFeature } from "@/gen/api/user";
import { STASH_LOCATION_CLEAR, getStashedLocation, State } from "@/reducers/stashLocation";
import { getUserProfile } from "@/reducers/userProfile";
import { genUrlWithHashid } from "@/utils/hashid";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import DotsVerticalIcon from '@/icons/DotsVertical';
import Create from "@/controller/Create";
import { useFormik } from "formik";
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { crudGetOneEx } from "@/actions/crudFetchEx";
import { nonSqlDbTypes } from "@/components/obfuscation/defs";


interface MoreActionDef {
  label: string,
  icon: typeof SvgIcon,
  btnProps: any,
}

export const MoreOptionsMenu: FC<{
  databaseInfo?: DatabaseResponseDto,
  permissions: Permissions_new[],
}> = ({
  databaseInfo,
  permissions,
}) => {

  const [states, setStates] = React.useState({
    anchorEl: null as Element | null,
    openClone: false,
  });
  
  const handleClick = (event: React.SyntheticEvent) => {
    setStates(v => ({...v, anchorEl: event.currentTarget }));
  };
  const handleMenuClose = () => {
    setStates(v => ({...v, anchorEl: null }));
  };

  const {
    showEncryptionLink,
    showESCLink,
    showDeployment,
    showDelete,
    showClone,
    deployment,
  } = useActionButtonStates(databaseInfo)
  const isNoSql = nonSqlDbTypes.has(databaseInfo?.databaseType!)

  const { handleDialogOpen, ConfirmDeleteDatabase } = useConfirmDeleteDatabase(databaseInfo, deployment)
  const handleDeleteDialogOpen = (e: any) => {
    handleDialogOpen(e)
    handleMenuClose()
  }

  const actions = ((
    databaseInfo ? [

    !isNoSql && {
      label: 'Backups',
      icon: QueueIcon,
      btnProps: {
        component: RouterLink,
        to: genUrlWithHashid(`/backups-n-validations/list/$0/show`, databaseInfo.databaseId || 0, databaseInfo.name),
      },
    },
    
    !isNoSql && showEncryptionLink && {
      label: 'Encryptions',
      icon: GradientIcon,
      btnProps: {
        component: RouterLink,
        to: genUrlWithHashid(`/encryption/list/$0/show`, databaseInfo.databaseId || 0, databaseInfo.name),
      },
    },
    
    !isNoSql && showDeployment && deployment && {
      label: 'Deployments',
      icon: UnarchiveIcon,
      btnProps: {
        component: RouterLink,
        to: genUrlWithHashid(`/deployments/list/$0/show`, deployment.deploymentId || 0, deployment.name),
      },
    },
    
    {
      label: 'Keys & Policy',
      icon: PolicyIcon,
      btnProps: {
        component: RouterLink,
        to: genUrlWithHashid(`/databases/$0/keys-policy`, databaseInfo.databaseId || 0, databaseInfo.name),
      },
    },

    !isNoSql && showClone && {
      label: 'Clone Database',
      icon: AddToDriveIcon,
      btnProps: {
        onClick: () => setStates(v => ({...v, openClone: true })),
      },
    },
    
    !isNoSql && showESCLink && {
      label: 'Encryption Setting Change',
      icon: RuleIcon,
      btnProps: {
        component: RouterLink,
        to: genUrlWithHashid(`/databases/$0/encryption-setting-change`, databaseInfo.databaseId || 0, databaseInfo.name),
      },
    },
    
    showDelete && {
      label: 'Delete',
      icon: DeleteForeverIcon,
      btnProps: {
        onClick: handleDeleteDialogOpen,
      },
    }
  ] : []) as MoreActionDef[]).filter(x => !!x)

  const cloneHandlers = useDefaultSavedHandlers("Clone Database")

  return (
    <>
      {(actions.length === 3 ? actions : actions.slice(0, 2)).flatMap(displayButton)}
      {actions.length > 3 &&
        <Tooltip title="More options">
          <IconButton 
            sx={{ 
              ml: 1,
              '&.MuiButtonBase-root': {
                mt: 0,
              },
              color: 'text.secondary',
            }}
            onClick={handleClick}
          >
            <DotsVerticalIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      {databaseInfo &&
        <>
          <Menu
            anchorEl={states.anchorEl}
            open={!!states.anchorEl}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '& .MuiListItemIcon-root': { minWidth: 'unset' },
              },
            }}
            MenuListProps={{
              sx: { pb: 0.5 }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            {actions.length > 3 && actions.slice(2, actions.length).map(displayMenuItem)}
          </Menu>
          {showDelete && ConfirmDeleteDatabase}
          {showClone &&
            <Create resource="database-clone" basePath="" {...cloneHandlers} >
              <CloneDatabase databaseInfo={databaseInfo} open={states.openClone} setOpen={(x:boolean) => setStates(v => ({...v, openClone: x}))} />
            </Create>
          }
        </>
      }
    </>
  )
}

interface ActionButtonStates {
  showEncryptionLink: boolean,
  showESCLink: boolean,
  showDeployment: boolean,
  showDelete: boolean,
  showClone: boolean,
  deployment: DeploymentResponseDto|undefined,
}

const useActionButtonStates = (databaseInfo: DatabaseResponseDto|undefined): ActionButtonStates => {
  const dispatch = useDispatch()
  const hasDBOpsPermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const isNoSql = nonSqlDbTypes.has(databaseInfo?.databaseType!)
  
  const { deploymentsFetched, deployment, ...buttonStates} = useSelector<ReduxState, Omit<ActionButtonStates, 'showDelete'|'showClone'>&{deploymentsFetched: boolean}>(store => {
    
    if (!databaseInfo?.databaseId) return {
      showEncryptionLink: false,
      deploymentsFetched: true,
      deployment: undefined,
      showESCLink: false,
      showDeployment: false,
    };
    
    const encryptDatabase: EncryptDbDto | undefined = store.admin.resources['encryption/list'].data[databaseInfo.databaseId] as any
    const showEncryptionLink = !isNoSql && !!encryptDatabase?.lastEncryptionTime
    
    const resData = store.admin.resources['deployments/list']
    const deploymentsFetched = !!resData && resData.list.loadedOnce
    const deployment : DeploymentResponseDto|undefined = 
      deploymentsFetched && resData.list.ids.flatMap(id => {
        const d : DeploymentResponseDto = resData.data[id] as any
        return d?.databaseId === databaseInfo.databaseId ? [d] : []
      }).pop() as any
    const userProfile = getUserProfile(store)
    const escData : EncryptionSettingChangesResponseDto|undefined = 
      store.admin.resources['databases/:id/encryption-setting-change'].data[databaseInfo.databaseId] as any
    const showESCLink = Boolean(
      userProfile && userProfile.features?.some(f => f.featureId === OnDataFeature.OnDataDriver) &&
      !!escData?.isChanged)
    const showDeployment = !isNoSql && Boolean(databaseInfo && databaseInfo.isManaged && deployment && deployment.deploymentStatus !== DeploymentStatus.Deleting)

    return {
      showEncryptionLink,
      deploymentsFetched,
      deployment,
      showESCLink,
      showDeployment,
    }
  })

  useEffect(() => {
    if (databaseInfo?.isManaged && !deploymentsFetched) {
      dispatch(crudGetList('deployments/list', pages(), sorts(), {}))
    }
  }, [dispatch, databaseInfo?.isManaged, deploymentsFetched])

  useEffect(() => {
    let timer: number|null = null
    if (!deployment) {
      timer && clearInterval(timer)
      timer = null
    } else {
      if (deployment.deploymentStatus === DeploymentStatus.Deleting) {
        timer = window.setInterval(() => {
          dispatch(crudGetList('deployments/list', pages(), sorts(), {}))
        }, 15000)
      }
    }

    return () => {
      timer && clearInterval(timer)
    }
  }, [deployment?.deploymentStatus, dispatch])

  useEffect(() => {
    databaseInfo?.databaseId && dispatch(crudGetOneEx('databases/:id/encryption-setting-change', databaseInfo?.databaseId, ''))
  }, [databaseInfo?.databaseId, dispatch])
  
  const showDelete = hasDBOpsPermit
  const showClone = hasDBOpsPermit && !(databaseInfo?.isManaged)

  return {
    ...buttonStates,
    showDelete,
    showClone,
    deployment,
  }
}

const useConfirmDeleteDatabase = (
  databaseInfo?: DatabaseResponseDto,
  deployment?: DeploymentResponseDto | null,
) => {
  const dispatch = useDispatch()
  
  useEffect(() => {
    dispatch(crudGetList('encryption/list', pages(), sorts(), {}))

    return () => {
      dispatch({ type: STASH_LOCATION_CLEAR })
    }
  }, [dispatch])

  const prevLocationRef = useRef(null as State)
  prevLocationRef.current = useSelector(getStashedLocation)
  const delHandlers = useDefaultSavedHandlers('Delete Database')
  delHandlers.onSuccess.merge(useCallback(() => 
    dispatch(push(prevLocationRef.current ? prevLocationRef.current.location : '/dashboard' as any))
  , [dispatch]))
  
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource: 'databases',
    record: { id: databaseInfo?.databaseId || -1 },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })

  const [dbNameConfirm, setDbNameConfirm] = useState("")
  const handleDbNameInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDbNameConfirm(e.target.value)
  }
  const dbNameConfirmed = dbNameConfirm?.toLowerCase() === databaseInfo?.name?.toLowerCase()

  const handleDeleteDialogClose = useCallback((e: any) => {
    handleDialogClose(e)
    setDbNameConfirm("")
  }, [handleDialogClose])

  const safeToDelete = !(databaseInfo?.isManaged) || (!deployment)
  
  const [toDeploymentState, handleDeleteOrRedirect] = useMemo(() => {
    const toDeploymentState = {
      pathname: genUrlWithHashid(`/deployments/list/$0/show`, deployment?.deploymentId || 0, deployment?.name),
      state: { from: 'DatabaseDetail' },
    }
    return [
      toDeploymentState,
      (e:any) => {
        if (safeToDelete) {
          handleDelete(e)
        } else if (deployment?.deploymentStatus === DeploymentStatus.Deleting) {
          handleDialogClose(e)
        } else if (deployment?.databaseId === databaseInfo?.databaseId) {
          dispatch(push(toDeploymentState))
        }
      }
    ] as const
  }, [safeToDelete, deployment, databaseInfo?.databaseId, handleDelete, handleDialogClose, dispatch])

  const ConfirmDeleteDatabase = (
    <Confirm
      isOpen={deleteDialogOpen}
      loading={loading}
      valid={safeToDelete ? dbNameConfirmed : (deployment?.databaseId === databaseInfo?.databaseId)}
      title={"Delete Database"}
      onConfirm={handleDeleteOrRedirect}
      onClose={handleDeleteDialogClose}
      confirm={safeToDelete ? "Confirm" 
        : (deployment?.deploymentStatus === DeploymentStatus.Deleting ? 'Close' 
        : 'Go to Deployment Detail')}
      cancel="Cancel"
      content={(
        safeToDelete ?
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Warning! Deleting database "{databaseInfo?.name}" is irreversible. The action you are about to take cannot be undone.
            </Typography>
          </Box>
          <MuiTextField
            id="confirm-database-name"
            autoFocus
            error={!dbNameConfirmed}
            helperText={!dbNameConfirmed && "Enter the name of your database to confirm delete."}
            size="medium"
            label="Type Database Name"
            sx={{ mb: 2, mt: 4, }}
            onChange={handleDbNameInput}
            value={dbNameConfirm}
          />
        </Box> 
        : (deployment?.databaseId === databaseInfo?.databaseId) ?
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
            {deployment?.deploymentStatus === DeploymentStatus.Deleting
            ? <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              This database can not be deleted because there is an deployment attached to it.
              We are tearing down the attached deployment, please wait for a few minutes then try to delete this 
              database again.
            </Typography>
            : <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              This database can not be deleted because there is an active deployment attached to it.
              If you wish to delete the database, please go to {" "}
              <Link component={RouterLink} to={toDeploymentState} underline="hover">
                deployment detail
              </Link> {" "}
              and perform deployment removal first.
            </Typography>
            }
          </Box>
        </Box> :
        null
      )}
    />
  )
  return {
    ConfirmDeleteDatabase,
    handleDialogOpen
  }
}

export interface CloneDatabaseProps {
  open: boolean;
  setOpen: (x:boolean) => void;
  databaseInfo: DatabaseResponseDto|undefined;
}

const validationSchema =
  Yup.object().shape({
    databaseName: Yup.string().max(255).required('New Database Name is required'),
    engineId: Yup.number().required('Engine is required'),
    description: Yup.string().max(255),
  })

const CloneDatabase : FC<CloneDatabaseProps> = ({
  databaseInfo,
  open,
  setOpen,
}) => {

  const handleClose = (): void => {
    setOpen(false);
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  const dispatch = useDispatch()
  
  const save = useSimpleSaveAction()

  const initialValues = {
    submit: '',
    databaseName: '',
    engineId: databaseInfo?.engineId || '',
    description: '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: ({data}:{data:any}) => {
        setStatus({ success: true })
        setSubmitting(false)
        setOpen(false)
        dispatch(push(genUrlWithHashid(`/databases/$0/show`, data.id || 0, values.databaseName)))
        dispatch(refreshView())
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const payload = {
      srcDatabase: databaseInfo,
      cloneInput: values,
    }
    save(payload, options)
  }, [save, databaseInfo])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm } = formikCtrl

  useEffect(() => {
    open && resetForm({
      values: {
        submit: '',
        databaseName: '',
        engineId: databaseInfo?.engineId || '',
        description: '',
      }
    })
  }, [open, databaseInfo, resetForm])

  const engines = useGetEngineList(databaseInfo)

  if (!databaseInfo) { return null }

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, }}}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }}>
        <CardHeader title="Clone Database" />
        <CardContent>

          <OdForm formikCtrl={formikCtrl}>
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                <Typography variant="body1" >
                  Clone database "{databaseInfo.name}"
                </Typography>
              </Grid>
              <Grid item xs={12} >
                <TextField label="New Database Name*" name="databaseName" />
              </Grid>
              <Grid item xs={12} >
                <SelectField label="Database Engine*" name="engineId" >
                  {engines.map(engine => (
                    <MenuItem key={engine.id} value={engine.id}>{engine.version} {engine.edition}</MenuItem>
                  ))}
                </SelectField>
              </Grid>
              <Grid item xs={12} >
                <TextField label="Description" name="description" />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                py: 2,
                mt: 2,
              }}
            >
              <Button
                color="primary"
                disabled={isSubmitting}
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={Object.keys(errors).some(s => s !== 'submit') || isSubmitting}
                type="submit"
                variant="contained"
              >
                Clone
              </Button>
            </Stack>
          </OdForm>

        </CardContent>
      </Card>
    </Drawer>
  )
}

const useGetEngineList = (databaseInfo: DatabaseResponseDto|undefined) => {
  const { data, ids } = useGetList<EngineResponseDto & RecPatch>(
    databaseInfo ? 'database-engine-versions' : 'noop', undefined, undefined, { databaseType: +(databaseInfo?.databaseType || '') })

  const srcEngine = databaseInfo?.engineId ? data[databaseInfo?.engineId] : undefined
  const engineList = useMemo(() => 
    ids.map(id => data[id]).filter(engine => {
      return (engine.version || '') >= (srcEngine?.version || '')
    }).sort((a, b) => (a.version || '') < (b.version || '') ? -1 : 1)
    
    , [ids, srcEngine, data])
  
  return engineList
}

const displayButton = (def:MoreActionDef) => {
  return (
    <Button
      key={def.label}
      size="small"
      variant="outlined"
      sx={{ ml: 1 }}
      {...def.btnProps}
      startIcon={<def.icon />}
      color={def.label === 'Delete' ? 'error' : undefined}
    >
      {def.label}
    </Button>
  )
}
const displayMenuItem = (def:MoreActionDef) => {
  if (def.label === 'Delete') {
    return [
      <Divider />,
      <MenuItem
        key={def.label}
        {...def.btnProps}
      >
        <ListItemIcon>
          <def.icon color="secondary" fontSize="small" />
        </ListItemIcon>
        {def.label}
      </MenuItem>
    ]
  }
  return [
    <MenuItem
      key={def.label}
      {...def.btnProps}
    >
      <ListItemIcon>
        <def.icon color="secondary" fontSize="small" />
      </ListItemIcon>
      {def.label}
    </MenuItem>
  ]
}
